import { FC } from "react";
import { styled } from "styled-components";
import { colors, font } from "../lib/sharedStyles";
import { useSearchContext } from "../contexts/SearchContext";
import SearchFlyoutProductCarousel from "./SearchFlyoutProductCarousel";

const OuterContainer = styled.div<{ $isVisible: boolean; $transformHeight: number; }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  overflow: scroll;
  height: ${({$transformHeight}) => `calc(100vh + ${$transformHeight}px)`};
  padding-top: ${({$transformHeight}) => `${$transformHeight * 2}px`};
  background: ${colors.fender.white};
  opacity: 0;
  pointer-events: none;
  transition: transform 300ms ease, opacity 300ms ease;

  @media (min-width: 1150px) {
    position: absolute;
    z-index: 1;
    top: 100%;
    height: auto;
    padding-top: 0;
    border-top: 1px solid ${colors.fender.grey02};
    overflow: initial;
    transform: translateY(-10px);
  }
  
  ${({ $isVisible }) => $isVisible && `
    opacity: 1;
    pointer-events: initial;

    @media (min-width: 1150px) {
      transform: translateY(0);
    }
  `}
`;

const InnerContainer = styled.div`
  padding: 16px 0 64px;

  @media (min-width: 1150px) {
    padding: 0;
    display: flex;

    &:after {
      content: '';
      position: absolute;
      bottom: -100vh;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.5);
      pointer-events: none;
    }
  }
`;

const ContentLeft = styled.div`
  padding: 0 0 16px 16px;

  @media (min-width: 1150px) {
    position: relative;
    flex: 0 0 35%;
    padding: 32px 0 40px;
  }
`;

const ContentRight = styled.div`
  overflow: hidden;
  
  @media (min-width: 1150px) {
    flex: 0 0 65%;
    padding: 32px 80px 40px;
    border-left: 1px solid ${colors.fender.grey03};
  }
`;

const SuggestionsContainer = styled.div`
  display: flex;
  flex-direction: column;

  p, a {
    margin: 0 0 16px;
    color: ${colors.fender.primaryDark};

    @media (min-width: 1150px) {
      padding-left: 85px;
    }
  }

  p {
    ${font('Instrument Sans', 600, '12px', '18px', '0.12px')};
  }

  a {
    ${font('Instrument Sans', 400, '14px', '20px', '0.14px')};
    text-decoration: none;

    span {
      font-weight: 600;
    }
    
    @media (min-width: 1150px) {
      padding-top: 6px;
      padding-bottom: 6px;
      margin-bottom: 4px;
    }

    // Only display three suggestions on mobile.
    &:nth-of-type(n+4) {
      @media (max-width: 1023px) {
        display: none;
      }
    }

    &:hover {
      background: ${colors.fender.grey01};
      text-decoration: underline;
    }
  }
`;

interface Props {
  isVisible: boolean;
  transformHeight: number;
}

const SearchFlyout: FC<Props> = ({ isVisible, transformHeight }) => {
  const { suggestions, searchInputValue } = useSearchContext();
  
  return (
    <OuterContainer $isVisible={isVisible} $transformHeight={transformHeight}>
      <InnerContainer>
        <ContentLeft>
          <SuggestionsContainer>
            <p>Suggested Searches</p>
            {suggestions.map((searchTerm, index) => {
              const inputIndex = searchTerm.indexOf(searchInputValue);
              const hasBoldText = searchInputValue && inputIndex >= 0;

              let boldPortion = '';
              let preBold = '';
              let postBold = '';
              if (hasBoldText) {
                boldPortion = searchTerm.substring(inputIndex, inputIndex + searchInputValue.length);
                preBold = searchTerm.substring(0, inputIndex);
                postBold = searchTerm.substring(inputIndex + searchInputValue.length);
              }

              return (
                <a 
                  key={`${searchTerm}-${index}`} 
                  href={`/search?q=${encodeURIComponent(searchTerm)}`}
                >
                  {(hasBoldText) ? (
                    <>{preBold}<span>{boldPortion}</span>{postBold}</>
                  ): (
                    <>{searchTerm}</>
                  )}
                </a>
              );
            })}
          </SuggestionsContainer>
        </ContentLeft>
        <ContentRight>
          {isVisible && (
            <SearchFlyoutProductCarousel />
          )}
        </ContentRight>
      </InnerContainer>
    </OuterContainer>
  );
}

export default SearchFlyout;